/**
 * AdminPageLayout - Standardized layout wrapper for admin pages
 * Provides consistent structure: breadcrumbs, header, actions, and content
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import PageLayout from './PageLayout';
import PageHeader from './PageHeader';
import PageActions, { PageActionsAlign, PageActionsGroup } from './PageActions';
import Breadcrumbs from './Breadcrumbs';
import Button from './Button';
import { ArrowLeft } from 'lucide-react';
import { generateBreadcrumbs } from '../utils/breadcrumbGenerator';

const AdminPageLayout = ({
  // Page identification
  title,
  subtitle,
  breadcrumbs = null, // Auto-generated if not provided

  // Breadcrumb customization
  breadcrumbOptions = {}, // Options for auto-generation: { currentPageLabel, dynamicLabels }

  // Action buttons
  backButton = true, // Show back button (can be true, false, or 'auto')
  backPath = '/admin/club',
  backLabel = '← Back', // Shorter label to save space - shows icon + text
  backLabelCompact = true, // Use compact label (just icon + "Back" instead of "Back to Dashboard")
  primaryActions = null, // Right-side action buttons (array or single element)
  secondaryActions = null, // Additional right-side buttons

  // Page content
  children,

  // Customization
  showBreadcrumbs = true,
  headerStyle = {},
  actionsStyle = {},
  className = '',
  maxWidth = '1400px',
  hideBackButtonIfBreadcrumbs = false, // Hide back button when breadcrumbs are present

  ...props
}) => {
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const finalBreadcrumbs = breadcrumbs || generateBreadcrumbs(
    location.pathname,
    {
      currentPageLabel: title,
      ...breadcrumbOptions
    }
  );

  // Determine if we should show the back button
  // If hideBackButtonIfBreadcrumbs is true and we have breadcrumbs, don't show back button
  const shouldShowBackButton = backButton && 
    !(hideBackButtonIfBreadcrumbs && showBreadcrumbs && finalBreadcrumbs && finalBreadcrumbs.length > 0);

  // Render actions if any are provided
  const hasActions = shouldShowBackButton || primaryActions || secondaryActions;

  return (
    <PageLayout
      maxWidth={maxWidth}
      className={`admin-page-layout ${className}`}
      {...props}
    >
      {/* Breadcrumbs Section - Standardized for Admin */}
      {showBreadcrumbs && finalBreadcrumbs && finalBreadcrumbs.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <Breadcrumbs items={finalBreadcrumbs} />
        </div>
      )}

      {/* Page Header - Consistent title and subtitle */}
      <PageHeader
        title={title}
        subtitle={subtitle}
        style={headerStyle}
      />

      {/* Page Actions - Navigation and primary tasks */}
      {hasActions && (
        <PageActions
          align={PageActionsAlign.SPACE_BETWEEN}
          style={{ marginBottom: '2rem', ...actionsStyle }}
        >
          {/* Left side - Back button */}
          {shouldShowBackButton && (
            <PageActionsGroup>
              <Button 
                to={backPath} 
                variant="ghost" 
                size="sm"
                title="Back to previous page"
                className="flex items-center gap-1"
              >
                <ArrowLeft size={16} /> 
                {backLabelCompact ? 'Back' : (backLabel.replace?.('← ', '') || backLabel)}
              </Button>
            </PageActionsGroup>
          )}

          {/* Right side - Primary and secondary actions */}
          {(primaryActions || secondaryActions) && (
            <PageActionsGroup>
              {secondaryActions && (
                Array.isArray(secondaryActions)
                  ? secondaryActions.map((action, idx) => <React.Fragment key={idx}>{action}</React.Fragment>)
                  : secondaryActions
              )}
              {primaryActions && (
                Array.isArray(primaryActions)
                  ? primaryActions.map((action, idx) => <React.Fragment key={idx}>{action}</React.Fragment>)
                  : primaryActions
              )}
            </PageActionsGroup>
          )}
        </PageActions>
      )}

      {/* Main Content Area */}
      <div className="admin-content-wrapper">
        {children}
      </div>
    </PageLayout>
  );
};

export default AdminPageLayout;
